/**
 * aigo-quick-add.js — Quantity Button for .qbtn product card steppers
 *
 * Manages cart state and drives the expand/collapse stepper UI.
 * Also handles legacy simple-add buttons (.m-pcard__add, .mm-pcard__add).
 */
(function () {
  // ── Cart state store ───────────────────────────────────────────────────────
  var _variantQtys = {};   // { variantId: quantity } — last known from server
  var _lineKeys    = {};   // { variantId: itemKey }  — for /cart/change.js
  var _pendingVids = {};   // { variantId: true }     — in-flight guard
  var _savedQtys   = {};   // { variantId: qty }      — pre-optimistic, for rollback

  function fetchCart() {
    // Use /cart.js directly — FoxTheme.routes.cart_url resolves to /cart (HTML),
    // not /cart.js (JSON). The .js suffix is required for the AJAX Cart API.
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (res) { return res.json(); });
  }

  function rebuildMap(cart) {
    _variantQtys = {};
    _lineKeys    = {};
    (cart.items || []).forEach(function (item) {
      var vid = String(item.variant_id);
      _variantQtys[vid] = (_variantQtys[vid] || 0) + item.quantity;
      _lineKeys[vid]    = item.key;  // last matching line item key wins
    });
  }

  // ── Button sync ────────────────────────────────────────────────────────────

  function syncButton(el, qty, bounce) {
    el.dataset.qty = qty;
    var count = el.querySelector('.qbtn__count');
    if (count) {
      count.textContent = qty;
      if (bounce && qty > 0) {
        count.classList.remove('is-bounce');
        void count.offsetWidth;   // force reflow so animation restarts
        count.classList.add('is-bounce');
      }
    }
    if (qty > 0) {
      el.classList.add('is-active');
    } else {
      el.classList.remove('is-active');
    }
  }

  function syncAll(vid, qty, bounce) {
    document.querySelectorAll('.qbtn[data-variant-id="' + vid + '"]').forEach(function (el) {
      syncButton(el, qty, bounce);
    });
    syncPcard(vid, qty, bounce);
  }

  function syncPcard(vid, qty, bounce) {
    document.querySelectorAll('.pcard__add[data-variant-id="' + vid + '"]').forEach(function (btn) {
      btn.style.display = qty > 0 ? 'none' : '';
    });
    document.querySelectorAll('.pcard__stepper[data-variant-id="' + vid + '"]').forEach(function (el) {
      if (qty > 0) {
        el.style.display = 'flex';
        var countEl = el.querySelector('.pcard__qty');
        if (countEl) {
          countEl.textContent = qty;
          if (bounce) {
            countEl.classList.remove('is-bounce');
            void countEl.offsetWidth;
            countEl.classList.add('is-bounce');
          }
        }
      } else {
        el.style.display = 'none';
      }
    });
  }

  function hydrateAll() {
    document.querySelectorAll('.qbtn[data-variant-id]').forEach(function (el) {
      var vid = el.dataset.variantId;
      syncButton(el, _variantQtys[vid] || 0, false);
    });
    // Hydrate pcard steppers (collection page cards)
    document.querySelectorAll('.pcard__stepper[data-variant-id]').forEach(function (el) {
      var vid = el.dataset.variantId;
      syncPcard(vid, _variantQtys[vid] || 0, false);
    });
  }

  // ── Toast ──────────────────────────────────────────────────────────────────
  var _toast      = null;
  var _toastTimer = null;

  function getToast() {
    if (_toast) return _toast;
    _toast = document.createElement('div');
    _toast.className = 'qa-toast';
    _toast.setAttribute('role', 'status');
    _toast.setAttribute('aria-live', 'polite');

    var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('width', '16'); icon.setAttribute('height', '16');
    icon.setAttribute('viewBox', '0 0 24 24'); icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor'); icon.setAttribute('stroke-width', '2.5');
    icon.setAttribute('stroke-linecap', 'round'); icon.setAttribute('stroke-linejoin', 'round');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20 6 9 17l-5-5');
    icon.appendChild(path);

    var label = document.createElement('span');
    _toast.appendChild(icon);
    _toast.appendChild(label);
    document.body.appendChild(_toast);
    return _toast;
  }

  function showToast(msg) {
    var t = getToast();
    t.querySelector('span').textContent = msg || 'Added to cart';
    clearTimeout(_toastTimer);
    t.classList.add('qa-toast--show');
    _toastTimer = setTimeout(function () { t.classList.remove('qa-toast--show'); }, 2200);
  }

  window.FoxTheme = window.FoxTheme || {};
  FoxTheme.showToast = showToast;

  var _style = document.createElement('style');
  _style.textContent = [
    '.qa-toast{position:fixed;bottom:80px;left:50%;transform:translateX(-50%) translateY(12px);',
    'display:flex;align-items:center;gap:8px;background:#1a1a1a;color:#fff;',
    'font-size:13px;font-weight:600;letter-spacing:.01em;padding:10px 16px;border-radius:999px;',
    'box-shadow:0 4px 16px rgba(0,0,0,.22);opacity:0;transition:opacity .18s,transform .18s;',
    'pointer-events:none;z-index:9999;white-space:nowrap;}',
    '.qa-toast--show{opacity:1;transform:translateX(-50%) translateY(0);}',
    '.qa-toast svg{flex-shrink:0;stroke:var(--orange-500,#FF8455);}'
  ].join('');
  document.head.appendChild(_style);

  // ── Increment (+) ──────────────────────────────────────────────────────────

  function handleAdd(container) {
    var vid = container.dataset.variantId;
    if (!vid || _pendingVids[vid]) return;

    _pendingVids[vid] = true;
    container.classList.add('is-loading');

    var prev       = _variantQtys[vid] || 0;
    _savedQtys[vid] = prev;
    var optimistic  = prev + 1;

    // Optimistic UI
    _variantQtys[vid] = optimistic;
    syncAll(vid, optimistic, true);

    // Use Cart module — Cart.add() throws on non-ok HTTP; shim dispatches cart:refresh automatically
    FoxTheme.Cart.add([{ id: parseInt(vid, 10), quantity: 1 }])
      .then(
        function (result) {
          // result = { state, sections }; result.state is the full cart
          // Full authoritative sync — also updates _variantQtys
          rebuildMap(result.state);
          syncAll(vid, _variantQtys[vid] || 0, false);
          // NO explicit cart:refresh dispatch — Cart module compatibility shim handles it
          if (prev === 0) showToast('Added to cart');
        },
        function (err) {
          console.error('[qbtn] Add error:', err);
          _variantQtys[vid] = _savedQtys[vid];
          syncAll(vid, _savedQtys[vid], false);
          showToast('Could not add to cart');
        }
      )
      .then(function () {
        container.classList.remove('is-loading');
        delete _pendingVids[vid];
      });
  }

  // ── Decrement (trash / minus) ──────────────────────────────────────────────

  function handleRemove(container) {
    var vid = container.dataset.variantId;
    if (!vid || _pendingVids[vid]) return;

    var current = _variantQtys[vid] || 0;
    if (current <= 0) return;

    _pendingVids[vid] = true;
    container.classList.add('is-loading');

    var newQty      = current - 1;
    _savedQtys[vid] = current;

    // Optimistic UI
    _variantQtys[vid] = newQty;
    syncAll(vid, newQty, true);

    var lineKey = _lineKeys[vid];

    function doChange(key) {
      // Use Cart module — Cart.change() throws on error; shim dispatches cart:refresh automatically
      FoxTheme.Cart.change(key, newQty)
        .then(
          function (result) {
            // result = { state, sections }; result.state is the full cart
            rebuildMap(result.state);
            syncAll(vid, _variantQtys[vid] || 0, false);
            // NO explicit cart:refresh dispatch — Cart module handles it
          },
          function (err) {
            console.error('[qbtn] Remove error:', err);
            _variantQtys[vid] = _savedQtys[vid];
            syncAll(vid, _savedQtys[vid], false);
          }
        )
        .then(function () {
          container.classList.remove('is-loading');
          delete _pendingVids[vid];
        });
    }

    if (lineKey) {
      doChange(lineKey);
    } else {
      // Line key not cached — re-fetch cart first
      fetchCart()
        .then(function (cart) {
          rebuildMap(cart);
          var key = _lineKeys[vid];
          if (key) {
            doChange(key);
          } else {
            // Item no longer in cart
            _variantQtys[vid] = 0;
            syncAll(vid, 0, false);
            container.classList.remove('is-loading');
            delete _pendingVids[vid];
          }
        })
        .catch(function (err) {
          console.error('[qbtn] Remove prefetch error:', err);
          _variantQtys[vid] = _savedQtys[vid];
          syncAll(vid, _savedQtys[vid], false);
          container.classList.remove('is-loading');
          delete _pendingVids[vid];
        });
    }
  }

  // ── Event delegation ───────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    // Guest guard — redirect to login for any ATC action
    if (e.target.closest('.qbtn__right, .qbtn__left, .m-pcard__add, .mm-pcard__add, .pcard__add, .pcard__add--qv, .pcard__step--inc, .pcard__step--dec')) {
      var body = document.body;
      if (body.dataset.loggedIn === 'false') {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = body.dataset.loginUrl + '?return_to=' + encodeURIComponent(window.location.pathname);
        return;
      }
    }

    // Quantity stepper — right (+) button
    var rightBtn = e.target.closest('.qbtn__right');
    if (rightBtn) {
      e.stopPropagation();
      var container = rightBtn.closest('.qbtn');
      if (container) handleAdd(container);
      return;
    }

    // Quantity stepper — left (trash/minus) button
    var leftBtn = e.target.closest('.qbtn__left');
    if (leftBtn) {
      e.stopPropagation();
      var container = leftBtn.closest('.qbtn');
      if (container) handleRemove(container);
      return;
    }

    // Quick-view button (multi-variant products)
    var qvBtn = e.target.closest('.pcard__add--qv');
    if (qvBtn) {
      e.stopPropagation();
      var handle = qvBtn.dataset.productHandle;
      var proxyId = qvBtn.dataset.proxyId || null;
      var compareAtPrice = qvBtn.dataset.compareAtPrice ? parseInt(qvBtn.dataset.compareAtPrice, 10) : null;
      if (handle && window.FoxTheme && FoxTheme.openQuickView) {
        FoxTheme.openQuickView(handle, proxyId, compareAtPrice);
      }
      return;
    }

    // pcard add button → show stepper and add 1
    var pcardAddBtn = e.target.closest('.pcard__add');
    if (pcardAddBtn && !pcardAddBtn.classList.contains('pcard__add--sold-out') && !pcardAddBtn.classList.contains('pcard__add--qv')) {
      e.stopPropagation();
      var row = pcardAddBtn.closest('.pcard__row');
      var stepper = row ? row.querySelector('.pcard__stepper') : null;
      if (stepper) handleAdd(stepper);
      return;
    }

    // pcard stepper increment
    var incStep = e.target.closest('.pcard__step--inc');
    if (incStep) {
      e.stopPropagation();
      var stepper = incStep.closest('.pcard__stepper');
      if (stepper) handleAdd(stepper);
      return;
    }

    // pcard stepper decrement
    var decStep = e.target.closest('.pcard__step--dec');
    if (decStep) {
      e.stopPropagation();
      var stepper = decStep.closest('.pcard__stepper');
      if (stepper) handleRemove(stepper);
      return;
    }

    // Legacy simple add buttons (mobile menu drawer, cart page popular products)
    var simpleBtn = e.target.closest('.m-pcard__add, .mm-pcard__add');
    if (simpleBtn) {
      e.stopPropagation();
      var vid = simpleBtn.dataset.variantId;
      if (!vid || simpleBtn.classList.contains('is-loading')) return;
      simpleBtn.classList.add('is-loading');
      // Use Cart module — shim dispatches cart:refresh automatically
      FoxTheme.Cart.add([{ id: parseInt(vid, 10), quantity: 1 }])
        .then(function (result) {
          showToast('Added to cart');
          simpleBtn.classList.add('is-added');
          setTimeout(function () { simpleBtn.classList.remove('is-added'); }, 1200);
          // Sync any qbtn for the same variant on the page using Cart module state
          rebuildMap(result.state);
          hydrateAll();
          simpleBtn.classList.remove('is-loading');
        })
        .catch(function (err) {
          console.error('[qbtn] Simple-add error:', err);
          showToast('Could not add to cart');
          simpleBtn.classList.remove('is-loading');
        });
    }
  });

  // ── Initialise on page load ────────────────────────────────────────────────

  function init() {
    if (!window.FoxTheme || !window.FoxTheme.routes) return;
    // Prefer Cart module state when available; fall back to direct fetch on page load
    var cached = window.FoxTheme.Cart && FoxTheme.Cart.getState();
    if (cached) {
      rebuildMap(cached);
      hydrateAll();
    } else {
      fetchCart()
        .then(function (cart) { rebuildMap(cart); hydrateAll(); })
        .catch(function (err) { console.warn('[qbtn] Cart fetch failed:', err); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ── External cart sync (cart drawer qty changes, etc.) ────────────────────

  function setupCartSubscription() {
    if (!window.FoxTheme || !window.FoxTheme.Cart) {
      console.warn('[qbtn] FoxTheme.Cart not available at DOMContentLoaded — falling back to cart:refresh');
      document.addEventListener('cart:refresh', function () {
        fetchCart().then(function (c) { rebuildMap(c); hydrateAll(); });
      });
      return;
    }

    var _cartUnsub = FoxTheme.Cart.subscribe(function (payload) {
      // Skip rehydration if a local mutation is in flight — handleAdd/doChange
      // already sync the UI and rebuildMap will run in their .then()
      if (Object.keys(_pendingVids).length > 0) return;
      var cart = payload.state;
      if (cart) {
        rebuildMap(cart);
        hydrateAll();
      } else {
        fetchCart().then(function (c) { rebuildMap(c); hydrateAll(); });
      }
    });

    // Collection facet pagination: aigo-facets.js fires this after DOM replacement
    document.addEventListener('aigo:collection:rerendered', function () {
      var cart = FoxTheme.Cart.getState();
      if (cart) { rebuildMap(cart); hydrateAll(); }
      else { fetchCart().then(function (c) { rebuildMap(c); hydrateAll(); }); }
    });
  }

  // Run after DOM ready (Cart module is available by then)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCartSubscription);
  } else {
    setupCartSubscription();
  }

  // Browser back/forward cache restore
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      // Prefer Cart module state; fall back to direct fetch if stale after bfcache
      var cart = window.FoxTheme && FoxTheme.Cart && FoxTheme.Cart.getState();
      if (cart) { rebuildMap(cart); hydrateAll(); }
      else { fetchCart().then(function (c) { rebuildMap(c); hydrateAll(); }); }
    }
  });
})();
